import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { StorageService } from '@/services/storage';

interface UserSettingsState {
  allowPushNotifications: boolean;
}

interface UserSettingsContextType {
  getSettings: () => UserSettingsState;
  setAllowPushNotifications: (allow: boolean) => void;
  isLoading: boolean;
}

const UserSettingsContext = createContext<UserSettingsContextType | undefined>(
  undefined
);

interface UserSettingsProviderProps {
  children: React.ReactNode;
}

const FLUSH_INTERVAL_MS = 2000;

const defaultSettings = (): UserSettingsState => ({
  allowPushNotifications: false,
});

function fromJSON(stored: string | null): UserSettingsState {
  if (!stored) return defaultSettings();
  try {
    const parsed = JSON.parse(stored);
    return { allowPushNotifications: parsed?.allowPushNotifications ?? false };
  } catch {
    return defaultSettings();
  }
}

export const UserSettingsProvider: React.FC<UserSettingsProviderProps> = ({
  children,
}) => {
  const [settings, setSettings] = useState<UserSettingsState>(defaultSettings);
  const [isLoading, setIsLoading] = useState(true);
  const needsFlushRef = useRef(false);

  useEffect(() => {
    const loadSettings = async () => {
      setIsLoading(true);
      try {
        const stored = await StorageService.getUserSettings();
        setSettings(fromJSON(stored));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load user settings:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadSettings();
  }, []);

  useEffect(() => {
    const flushToStorage = async () => {
      if (needsFlushRef.current) {
        try {
          await StorageService.setUserSettings(JSON.stringify(settings));
          needsFlushRef.current = false;
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to flush user settings:', error);
        }
      }
    };

    const intervalId = setInterval(flushToStorage, FLUSH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [settings]);

  useEffect(() => {
    needsFlushRef.current = true;
  }, [settings]);

  const setAllowPushNotifications = useCallback((allow: boolean) => {
    const newSettings: UserSettingsState = { allowPushNotifications: allow };
    setSettings(newSettings);
    needsFlushRef.current = true;
    StorageService.setUserSettings(JSON.stringify(newSettings)).catch(error => {
      // eslint-disable-next-line no-console
      console.error('Failed to save user settings immediately:', error);
    });
  }, []);

  const getSettings = useCallback(() => settings, [settings]);

  const value: UserSettingsContextType = useMemo(
    () => ({ getSettings, setAllowPushNotifications, isLoading }),
    [getSettings, setAllowPushNotifications, isLoading]
  );

  return (
    <UserSettingsContext.Provider value={value}>
      {children}
    </UserSettingsContext.Provider>
  );
};

export const useUserSettings = (): UserSettingsContextType => {
  const context = useContext(UserSettingsContext);
  if (context === undefined) {
    throw new Error(
      'useUserSettings must be used within a UserSettingsProvider'
    );
  }
  return context;
};
